import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { LocationDto, StockMovementDto, VariantStockDetailDto, VariantStockDto, WarehouseDto } from '@thebb/shared';
import { get, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, PageTitle, Spinner, fmtTime } from '../components/ui';
import { locationTypeLabel } from './Locations';

export const movementTypeLabel: Record<string, string> = {
  receipt: 'Modtagelse',
  pick: 'Pluk',
  pack_adjust: 'Pakkejustering',
  transfer_out: 'Overførsel ud',
  transfer_in: 'Overførsel ind',
  count: 'Optælling',
  adjust: 'Justering',
  return: 'Retur',
  scrap: 'Kassation',
  migration: 'Åbningsbalance',
};
const reasonLabel: Record<string, string> = { count_correction: 'Tællefejl', damaged: 'Beskadiget', lost: 'Bortkommet', found: 'Fundet', sample: 'Vareprøve', expired: 'Udløbet', other: 'Andet' };

/** Products with on hand, reserved, available per warehouse; the availability formula from the data model. */
export function StockPage() {
  const [q, setQ] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [withStock, setWithStock] = useState(true);
  const [page, setPage] = useState(1);
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (warehouseId) p.set('warehouseId', warehouseId);
    p.set('withStock', String(withStock));
    p.set('page', String(page));
    return p.toString();
  }, [q, warehouseId, withStock, page]);
  const list = useQuery({ queryKey: ['stock', params], queryFn: () => get<{ items: VariantStockDto[]; total: number; perPage: number }>(`/stock?${params}`) });
  const whs = (warehouses.data ?? []).filter((w) => w.active && (!warehouseId || w.id === warehouseId));
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.perPage)) : 1;

  return (
    <div>
      <PageTitle
        title="Lager"
        actions={
          <>
            <input className="input w-56" placeholder="SKU eller navn" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            <select className="input w-40" value={warehouseId} onChange={(e) => { setWarehouseId(e.target.value); setPage(1); }}>
              <option value="">Alle lagre</option>
              {warehouses.data?.map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
            </select>
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={withStock} onChange={(e) => { setWithStock(e.target.checked); setPage(1); }} /> Kun med beholdning</label>
          </>
        }
      />
      {list.isLoading ? (
        <Spinner />
      ) : (
        <table className="table card p-0">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Vare</th>
              {whs.map((w) => <th key={w.id} className="text-right" title={w.name}>{w.code}</th>)}
              <th className="text-right">På lager</th>
              <th className="text-right">Reserveret</th>
              <th className="text-right">Disponibelt</th>
              <th className="text-right">Kan kaldes hjem</th>
              <th className="text-right" title="Navision-beholdning fra YouWe-broen">Navision</th>
            </tr>
          </thead>
          <tbody>
            {list.data?.items.map((v) => (
              <tr key={v.variantId}>
                <td><Link to="/stock/$variantId" params={{ variantId: v.variantId }} className="font-medium text-blue-700 hover:underline">{v.sku}</Link></td>
                <td>{v.productTitle}{v.variantTitle && <span className="text-zinc-500"> · {v.variantTitle}</span>}{!v.active && <Badge>inaktiv</Badge>}</td>
                {whs.map((w) => {
                  const c = v.warehouses.find((x) => x.warehouseId === w.id);
                  return (
                    <td key={w.id} className="text-right tabular-nums" title={c ? `${c.onHand} på lager, ${c.reserved} reserveret` : ''}>
                      {c ? (c.available === null ? <span className="text-zinc-500">{c.onHand}</span> : c.available) : 0}
                      {c && c.reserved > 0 && <span className="text-xs text-zinc-400"> ({c.onHand})</span>}
                    </td>
                  );
                })}
                <td className="text-right tabular-nums">{v.onHand}</td>
                <td className="text-right tabular-nums">{v.reserved}</td>
                <td className="text-right tabular-nums font-medium">{v.sellable}</td>
                <td className="text-right tabular-nums text-zinc-500">{v.callable}</td>
                <td className={`text-right tabular-nums ${v.navision && v.navision.quantity !== v.onHand ? 'text-amber-700' : 'text-zinc-500'}`} title={v.navision ? [v.navision.locationCode, v.navision.binCode].filter(Boolean).join(' ') : ''}>{v.navision ? v.navision.quantity : '–'}</td>
              </tr>
            ))}
            {list.data?.items.length === 0 && <tr><td colSpan={7 + whs.length} className="text-zinc-500">Ingen varer med beholdning. Start med en åbningsoptælling under Optælling.</td></tr>}
          </tbody>
        </table>
      )}
      {pages > 1 && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Forrige</button>
          <span>Side {page} af {pages}</span>
          <button className="btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>Næste</button>
        </div>
      )}
    </div>
  );
}

/** One variant: balances per location, reservations, the ledger, and a manual adjustment. */
export function StockVariantPage() {
  const { variantId } = useParams({ from: '/app/stock/$variantId' });
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const detail = useQuery({ queryKey: ['stock-variant', variantId], queryFn: () => get<VariantStockDetailDto>(`/stock/variants/${variantId}`) });
  const ledger = useQuery({ queryKey: ['stock-movements', variantId], queryFn: () => get<{ items: StockMovementDto[]; total: number }>(`/stock/movements?variantId=${variantId}&perPage=100`) });
  const [adj, setAdj] = useState({ warehouseId: '', locationId: '', quantity: '', type: 'adjust', reasonCode: 'count_correction', note: '' });
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const locations = useQuery({ queryKey: ['locations', adj.warehouseId], queryFn: () => get<{ items: LocationDto[] }>(`/locations?warehouseId=${adj.warehouseId}&perPage=500`), enabled: !!adj.warehouseId });
  const adjust = useMutation({
    mutationFn: () => post('/stock/adjust', { variantId, locationId: adj.locationId, quantity: Number(adj.quantity), type: adj.type, reasonCode: adj.reasonCode, note: adj.note || null }),
    onSuccess: () => {
      setAdj({ ...adj, quantity: '', note: '' });
      void qc.invalidateQueries({ queryKey: ['stock-variant', variantId] });
      void qc.invalidateQueries({ queryKey: ['stock-movements', variantId] });
      void qc.invalidateQueries({ queryKey: ['stock'] });
    },
    onError: setError,
  });
  const d = detail.data;
  if (!d) return <Spinner />;
  return (
    <div className="space-y-6">
      <PageTitle title={`${d.sku} · ${d.productTitle}${d.variantTitle ? ` · ${d.variantTitle}` : ''}`} actions={<Link to="/stock" className="btn">Tilbage</Link>} />
      <ErrorBox error={error} />
      <div className="grid gap-4 md:grid-cols-4">
        <div className="card text-sm"><div className="label">På lager</div><div className="text-2xl font-semibold tabular-nums">{d.onHand}</div></div>
        <div className="card text-sm"><div className="label">Reserveret</div><div className="text-2xl font-semibold tabular-nums">{d.reserved}</div></div>
        <div className="card text-sm"><div className="label">Disponibelt</div><div className="text-2xl font-semibold tabular-nums">{d.sellable}</div></div>
        <div className="card text-sm"><div className="label">Kan kaldes hjem</div><div className="text-2xl font-semibold tabular-nums">{d.callable}</div></div>
      </div>
      {d.navision && (
        <div className="card text-sm">
          <div className="label">Navision (hentet {fmtTime(d.navision.fetchedAt)})</div>
          <div className="flex flex-wrap gap-6">
            <span>Beholdning <b className="tabular-nums">{d.navision.quantity}</b></span>
            <span>Åbne salg <b className="tabular-nums">{d.navision.sales}</b></span>
            <span>Åbne indkøb <b className="tabular-nums">{d.navision.purchases}</b></span>
            {d.navision.locationCode && <span>Lager <b>{d.navision.locationCode}</b></span>}
            {d.navision.binCode && <span>Placering <b>{d.navision.binCode}</b></span>}
            {d.navision.productGroup && <span>Gruppe <b>{d.navision.productGroup}</b></span>}
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">Pr. lager</h2>
          <table className="table card p-0">
            <thead><tr><th>Lager</th><th className="text-right">På lager</th><th className="text-right">Reserveret</th><th className="text-right">Disponibelt</th></tr></thead>
            <tbody>
              {d.warehouses.map((w) => (
                <tr key={w.warehouseId}><td>{w.warehouseCode} {w.warehouseType === 'remote' && <Badge>fjern</Badge>}</td><td className="text-right tabular-nums">{w.onHand}</td><td className="text-right tabular-nums">{w.reserved}</td><td className="text-right tabular-nums">{w.available ?? '–'}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">Lokationer</h2>
          <table className="table card p-0">
            <thead><tr><th>Lager</th><th>Lokation</th><th>Type</th><th className="text-right">Antal</th></tr></thead>
            <tbody>
              {d.locations.map((l) => <tr key={l.locationId}><td>{l.warehouseCode}</td><td className="font-medium">{l.locationCode}</td><td>{locationTypeLabel[l.locationType]}</td><td className="text-right tabular-nums">{l.onHand}</td></tr>)}
              {d.locations.length === 0 && <tr><td colSpan={4} className="text-zinc-500">Ingen beholdning.</td></tr>}
            </tbody>
          </table>
          {d.reservations.length > 0 && (
            <>
              <h2 className="mb-2 mt-4 text-sm font-semibold uppercase tracking-wide text-zinc-500">Reservationer</h2>
              <table className="table card p-0">
                <thead><tr><th>Lager</th><th>Type</th><th>Reference</th><th className="text-right">Antal</th></tr></thead>
                <tbody>{d.reservations.map((r) => <tr key={r.id}><td>{r.warehouseCode}</td><td>{r.kind}</td><td className="text-xs text-zinc-500">{r.refType} {r.refId}</td><td className="text-right tabular-nums">{r.quantity}</td></tr>)}</tbody>
              </table>
            </>
          )}
        </section>
      </div>

      {can('stock.adjust') && (
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold">Justér beholdning</h2>
          <div className="grid gap-3 md:grid-cols-6">
            <div>
              <label className="label">Lager</label>
              <select className="input" value={adj.warehouseId} onChange={(e) => setAdj({ ...adj, warehouseId: e.target.value, locationId: '' })}>
                <option value="">Vælg</option>
                {warehouses.data?.filter((w) => w.active).map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Lokation</label>
              <select className="input" value={adj.locationId} onChange={(e) => setAdj({ ...adj, locationId: e.target.value })} disabled={!adj.warehouseId}>
                <option value="">Vælg</option>
                {locations.data?.items.map((l) => <option key={l.id} value={l.id}>{l.code}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Antal (+/−)</label>
              <input className="input" type="number" value={adj.quantity} onChange={(e) => setAdj({ ...adj, quantity: e.target.value })} />
            </div>
            <div>
              <label className="label">Type</label>
              <select className="input" value={adj.type} onChange={(e) => setAdj({ ...adj, type: e.target.value })}>
                <option value="adjust">Justering</option>
                <option value="scrap">Kassation</option>
              </select>
            </div>
            <div>
              <label className="label">Årsag</label>
              <select className="input" value={adj.reasonCode} onChange={(e) => setAdj({ ...adj, reasonCode: e.target.value })}>
                {Object.entries(reasonLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button className="btn btn-primary w-full" disabled={!adj.locationId || !adj.quantity || Number(adj.quantity) === 0 || adjust.isPending} onClick={() => adjust.mutate()}>Bogfør</button>
            </div>
            <div className="md:col-span-6">
              <input className="input" placeholder="Note" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} />
            </div>
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">Bevægelser</h2>
        <table className="table card p-0">
          <thead><tr><th>Tid</th><th>Type</th><th>Lager</th><th>Lokation</th><th className="text-right">Antal</th><th>Årsag</th><th>Reference</th><th>Af</th></tr></thead>
          <tbody>
            {ledger.data?.items.map((m) => (
              <tr key={m.id}>
                <td className="whitespace-nowrap">{fmtTime(m.occurredAt)}</td>
                <td>{movementTypeLabel[m.type] ?? m.type}</td>
                <td>{m.warehouseCode}</td>
                <td>{m.locationCode}</td>
                <td className={`text-right tabular-nums ${m.quantity < 0 ? 'text-red-700' : 'text-green-700'}`}>{m.quantity > 0 ? '+' : ''}{m.quantity}</td>
                <td className="text-zinc-600">{m.reasonCode ? (reasonLabel[m.reasonCode] ?? m.reasonCode) : ''}{m.note && <span className="text-zinc-400"> · {m.note}</span>}</td>
                <td className="text-xs text-zinc-500">{m.refType && `${m.refType}`}</td>
                <td>{m.actorName}</td>
              </tr>
            ))}
            {ledger.data?.items.length === 0 && <tr><td colSpan={8} className="text-zinc-500">Ingen bevægelser.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
